import { DateTime } from 'luxon';

import {
  constructDynamicInterval,
  constructPredefinedInterval,
  type DynamicRangeIntervalTimestep,
  type GeneratedReportRanges,
  isGeneratedReportRange,
  isGeneratedReportRangeTypeDynamic,
  normalizeGeneratedReportRange,
  type PREDEFINED_RANGE_VALUE,
} from 'app-components/utils/date';
import type { CanonicalRecentRange } from 'app-components/utils/date/predefinedRange';

import {
  type AdditionalComparisonColumnType,
  type ComparisonRange,
  type ComparisonRangeDateRange,
  type ComparisonRangeLabel,
  PRIMARY_RANGE_ID,
  type PrimaryAdditionalColumnType,
  type ReportConfigurationInputProps,
} from '../reportConfigurationsTypes';

// ================================================================================================
// Comparison range helpers
//
// All pure, all operating on plain `ComparisonRange[]`. Ranges live inside an Immutable Record but
// are themselves plain objects — Records only wrap the top level — so these never use Record APIs.
//
// Every function that can empty the list returns `[]`, never `undefined`. `upsertResource` merges
// via `mergeDeepEx`, whose `resolveConflicts` step only replaces an existing array when the
// incoming value is truthy; an `undefined` leaves the previous array in place, and Immutable's
// `mergeDeepWith` would otherwise concatenate the two.
// ================================================================================================

/** `rangeId` must be an uppercase identifier, and `PRIMARY` is reserved for the primary range. */
export const RANGE_ID_PATTERN = /^[A-Z][A-Z0-9_]*$/;

const RANGE_ID_PREFIX = 'RANGE_';

/**
 * Generate a `rangeId` unique within `existingRanges`.
 *
 * Called once when a range is created and never again: `rangeId` is the column identity used by
 * the generated report, so regenerating it on a label edit would churn every column and remount
 * the grid. It is deliberately not derived from the label for the same reason.
 */
export const generateRangeId = (existingRanges: ComparisonRange[] = []): string => {
  const taken = new Set(existingRanges.map((range) => range.rangeId));
  let index = existingRanges.length + 1;
  let candidate = `${RANGE_ID_PREFIX}${index}`;
  while (taken.has(candidate) || candidate === PRIMARY_RANGE_ID) {
    index += 1;
    candidate = `${RANGE_ID_PREFIX}${index}`;
  }
  return candidate;
};

/**
 * The range a report gets when it is switched to COMPARISON without one.
 *
 * A comparison report is meaningless with zero ranges — and invalid per
 * `validateComparisonRanges` — so entering COMPARISON always seeds one rather than leaving the
 * user on a broken config. `PRIOR_PERIOD` is the neutral choice: it is defined relative to
 * whatever primary range the report already has, so it needs no dates of its own and stays
 * correct if the primary range later changes.
 *
 * `DELTA_AMOUNT` ships on by default because the difference between the two periods is the reason
 * to run a comparison at all — a report that shows both values and makes the user work out the
 * change themselves is the wrong default. It is also what the chart's Difference column and the
 * header's delta report, so the grid agrees with them out of the box. The other two derived
 * columns stay opt-in: cumulative values only mean something read period by period, and the
 * percentage is redundant beside the amount for most reports.
 */
export const mkDefaultComparisonRange = (existingRanges: ComparisonRange[] = []): ComparisonRange => ({
  rangeId: generateRangeId(existingRanges),
  label: { type: 'AUTO' },
  dateRange: 'PRIOR_PERIOD',
  additionalColumnTypes: ['DELTA_AMOUNT'],
});

/**
 * Remove duplicates while preserving order.
 *
 * Order is semantic for `additionalColumnTypes` — it determines generated column order — so a
 * `new Set([...])` round-trip would silently reorder as well as dedupe.
 */
const dedupePreservingOrder = <T>(values: readonly T[]): T[] => {
  const seen = new Set<T>();
  return values.filter((value) => (seen.has(value) ? false : (seen.add(value), true)));
};

/**
 * Deep copy so a cloned config never shares mutable range objects with its source.
 *
 * Both nested fields are copied only when the source actually has them. Assigning `undefined`
 * unconditionally would add an own key, and `compareReportConfigs` uses lodash `isEqual`, which
 * counts own keys — a cloned range would then never equal the JSON-parsed one it came from, so
 * every clone would read as modified and leave Save / Reset permanently lit. `label` is required by
 * the type but can be missing or `null` on an untyped response, and spreading one of those into
 * `{ ...label }` would invent a `label: {}` with the same effect. `validateComparisonRanges`
 * reports the missing label; this function's job is only to not corrupt it.
 */
export const cloneComparisonRange = (range: ComparisonRange): ComparisonRange => {
  const clone: ComparisonRange = { ...range };
  if (range.label && typeof range.label === 'object') {
    clone.label = { ...range.label };
  }
  if (range.additionalColumnTypes) {
    clone.additionalColumnTypes = [...range.additionalColumnTypes];
  } else {
    // The spread above copies the key even when its value is undefined.
    delete clone.additionalColumnTypes;
  }
  return clone;
};

export const cloneComparisonRanges = (ranges: ComparisonRange[] | undefined): ComparisonRange[] | undefined =>
  ranges ? ranges.map(cloneComparisonRange) : undefined;

export const addComparisonRange = (
  ranges: ComparisonRange[] | undefined,
  range: Omit<ComparisonRange, 'rangeId'> & { rangeId?: string },
): ComparisonRange[] => {
  const current = ranges ?? [];
  return [
    ...current.map(cloneComparisonRange),
    cloneComparisonRange({ ...range, rangeId: range.rangeId ?? generateRangeId(current) }),
  ];
};

export const updateComparisonRange = (
  ranges: ComparisonRange[] | undefined,
  rangeId: string,
  updates: Partial<Omit<ComparisonRange, 'rangeId'>>,
): ComparisonRange[] =>
  (ranges ?? []).map((range) =>
    range.rangeId === rangeId ? cloneComparisonRange({ ...range, ...updates }) : cloneComparisonRange(range),
  );

export const removeComparisonRange = (ranges: ComparisonRange[] | undefined, rangeId: string): ComparisonRange[] =>
  (ranges ?? []).filter((range) => range.rangeId !== rangeId).map(cloneComparisonRange);

/** Move the range at `fromIndex` to `toIndex`. Out-of-bounds indices leave the order unchanged. */
export const reorderComparisonRanges = (
  ranges: ComparisonRange[] | undefined,
  fromIndex: number,
  toIndex: number,
): ComparisonRange[] => {
  const next = (ranges ?? []).map(cloneComparisonRange);
  if (fromIndex < 0 || fromIndex >= next.length || toIndex < 0 || toIndex >= next.length || fromIndex === toIndex) {
    return next;
  }
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

/**
 * Clear the date sub-fields that no longer apply to a range's `dateRange` mode.
 *
 * Mirrors `clearConflictingDateRangeFields` in `reportFieldAvailability.ts`, but ranges are plain
 * objects rather than Record values, so this rebuilds the object instead of merging `null`s. The
 * contract rejects irrelevant date fields rather than ignoring them, so hiding the inputs in the
 * UI is not sufficient — the values have to go.
 */
export const clearConflictingRangeDateFields = (range: ComparisonRange): ComparisonRange => {
  const { dateRange } = range;
  const next: ComparisonRange = { ...range };

  if (dateRange !== 'CUSTOM' && dateRange !== 'CUSTOM_SINCE') {
    delete next.startOn;
  }
  if (dateRange !== 'CUSTOM') {
    delete next.endOn;
  }
  if (dateRange !== 'CUSTOM_LAST' && dateRange !== 'CUSTOM_RECENT') {
    delete next.dateRangeInterval;
    delete next.dateRangeIntervalCount;
  }

  return next;
};

export const clearConflictingRangeDateFieldsAll = (ranges: ComparisonRange[] | undefined): ComparisonRange[] =>
  (ranges ?? []).map(clearConflictingRangeDateFields);

// ================================================================================================
// Validation
// ================================================================================================

export type ComparisonRangeFieldError = {
  /** Index into the ranges array, or `null` for list-level problems. */
  index: number | null;
  rangeId?: string;
  field:
    | 'rangeId'
    | 'label'
    | 'dateRange'
    | 'startOn'
    | 'endOn'
    | 'dateRangeInterval'
    | 'dateRangeIntervalCount'
    | 'additionalColumnTypes'
    | 'comparisonRanges'
    | 'primaryAdditionalColumnTypes';
  message: string;
};

export const isCustomLabel = (label: ComparisonRangeLabel | undefined): label is { type: 'CUSTOM'; value: string } =>
  label?.type === 'CUSTOM';

const isRelativeMode = (dateRange: ComparisonRangeDateRange): boolean =>
  dateRange === 'PRIOR_PERIOD' || dateRange === 'PRIOR_YEAR_PERIOD';

const hasDuplicates = <T>(values: readonly T[]): boolean => new Set(values).size !== values.length;

/**
 * Validate the comparison configuration, mirroring the server contract so the user gets
 * field-level feedback instead of a bare 400.
 *
 * Returns every problem found rather than short-circuiting; an empty array means valid.
 */
export const validateComparisonRanges = (
  ranges: ComparisonRange[] | undefined,
  primaryAdditionalColumnTypes?: PrimaryAdditionalColumnType[],
): ComparisonRangeFieldError[] => {
  const errors: ComparisonRangeFieldError[] = [];

  // Server responses are passed through untyped, so treat every input as untrusted: this runs
  // inside a selector and must report a problem rather than throw on a malformed payload.
  if (ranges !== undefined && !Array.isArray(ranges)) {
    return [{ index: null, field: 'comparisonRanges', message: 'Comparison ranges must be a list.' }];
  }

  if (!ranges || ranges.length === 0) {
    errors.push({
      index: null,
      field: 'comparisonRanges',
      message: 'A comparison report requires at least one comparison range.',
    });
  }

  if (primaryAdditionalColumnTypes !== undefined && !Array.isArray(primaryAdditionalColumnTypes)) {
    errors.push({
      index: null,
      field: 'primaryAdditionalColumnTypes',
      message: 'Additional column types must be a list.',
    });
  } else if (primaryAdditionalColumnTypes && hasDuplicates(primaryAdditionalColumnTypes)) {
    errors.push({
      index: null,
      field: 'primaryAdditionalColumnTypes',
      message: 'Duplicate additional column types are not allowed.',
    });
  }

  const seenRangeIds = new Set<string>();

  (ranges ?? []).forEach((range, index) => {
    if (!range || typeof range !== 'object') {
      errors.push({ index, field: 'comparisonRanges', message: 'A comparison range must be an object.' });
      return;
    }

    const { rangeId, label, dateRange, startOn, endOn, dateRangeInterval, dateRangeIntervalCount } = range;
    const at = (field: ComparisonRangeFieldError['field'], message: string): ComparisonRangeFieldError => ({
      index,
      rangeId,
      field,
      message,
    });

    // --- rangeId ---
    if (!rangeId) {
      errors.push(at('rangeId', 'A range id is required.'));
    } else if (rangeId === PRIMARY_RANGE_ID) {
      errors.push(at('rangeId', `"${PRIMARY_RANGE_ID}" is reserved for the primary range.`));
    } else if (!RANGE_ID_PATTERN.test(rangeId)) {
      errors.push(at('rangeId', 'A range id must start with a letter and use only A-Z, 0-9 and underscores.'));
    } else if (seenRangeIds.has(rangeId)) {
      errors.push(at('rangeId', `Duplicate range id "${rangeId}".`));
    }
    if (rangeId) {
      seenRangeIds.add(rangeId);
    }

    // --- label ---
    // `label` is required by the type, but responses are passed through untyped, so a range can
    // reach here without one. `cloneComparisonRange` leaves a missing label missing rather than
    // fabricating `{}`, which keeps clone/source `isEqual` — this is what reports the problem.
    if (!label || typeof label !== 'object') {
      errors.push(at('label', 'A range label is required.'));
    } else if (label.type !== 'AUTO' && label.type !== 'CUSTOM') {
      errors.push(at('label', 'A range label must be either automatic or custom.'));
    } else if (isCustomLabel(label) && (typeof label.value !== 'string' || !label.value.trim())) {
      errors.push(at('label', 'A custom range label cannot be blank.'));
    }

    // --- dateRange ---
    if (!dateRange) {
      errors.push(at('dateRange', 'A date range is required.'));
      return;
    }

    // --- date fields ---
    // `PRIOR_PERIOD` / `PRIOR_YEAR_PERIOD` are resolved against the primary range and carry no
    // dates of their own, so they have nothing to check here.
    if (!isRelativeMode(dateRange)) {
      if (!isGeneratedReportRange(dateRange)) {
        // A stale saved config, a value from a newer picker, or a malformed response. Reject it
        // here rather than casting: no downstream `switch` has an arm for it, so an unrecognized
        // range would otherwise reach the server as a bare 400.
        errors.push(at('dateRange', `Unrecognized date range "${String(dateRange)}".`));
      } else if (dateRange === 'CUSTOM') {
        if (!startOn) errors.push(at('startOn', 'A custom range requires a start date.'));
        if (!endOn) errors.push(at('endOn', 'A custom range requires an end date.'));
        if (startOn && endOn && startOn > endOn) {
          errors.push(at('endOn', 'The end date must be on or after the start date.'));
        }
      } else if (dateRange === 'CUSTOM_SINCE') {
        if (!startOn) errors.push(at('startOn', 'A "since" range requires a start date.'));
      } else if (isGeneratedReportRangeTypeDynamic(dateRange)) {
        // CUSTOM_LAST / CUSTOM_RECENT
        if (!dateRangeInterval) {
          errors.push(at('dateRangeInterval', 'A rolling range requires an interval.'));
        }
        if (dateRangeIntervalCount === undefined || dateRangeIntervalCount === null) {
          errors.push(at('dateRangeIntervalCount', 'A rolling range requires an interval count.'));
        } else if (!Number.isInteger(dateRangeIntervalCount) || dateRangeIntervalCount < 1) {
          errors.push(at('dateRangeIntervalCount', 'The interval count must be a whole number of at least 1.'));
        }
      }
    }

    // --- additional columns ---
    if (range.additionalColumnTypes !== undefined && !Array.isArray(range.additionalColumnTypes)) {
      errors.push(at('additionalColumnTypes', 'Additional column types must be a list.'));
    } else if (range.additionalColumnTypes && hasDuplicates(range.additionalColumnTypes)) {
      errors.push(at('additionalColumnTypes', 'Duplicate additional column types are not allowed.'));
    }
  });

  return errors;
};

/** Order-preserving dedupe for the two additional-column arrays. */
export const normalizeAdditionalColumnTypes = <T extends AdditionalComparisonColumnType | PrimaryAdditionalColumnType>(
  types: readonly T[] | undefined,
): T[] | undefined => (types ? dedupePreservingOrder(types) : undefined);

/** A resolved date span, both ends inclusive and ISO `yyyy-MM-dd`. */
export type ResolvedRangeDates = { startOn: string; endOn: string };

/** The resolved primary span plus the preset metadata QCS uses to choose comparison semantics. */
export type ResolvedPrimaryRangeDates = ResolvedRangeDates & {
  dateRange?: GeneratedReportRanges;
  dateRangeInterval?: DynamicRangeIntervalTimestep;
  dateRangeIntervalCount?: number;
};

type RecentIntervalCount = { interval: DynamicRangeIntervalTimestep; count: number };

/**
 * Fixed presets that include the current, in-progress period and therefore use the same comparison
 * semantics as CUSTOM_RECENT. LAST_7_MONTHS and LAST_24_MONTHS are deliberately absent because QCS
 * does not classify those frontend-only presets as recent ranges.
 */
const RECENT_PRESETS: Partial<Record<GeneratedReportRanges, RecentIntervalCount>> = {
  RECENT_7_DAYS: { interval: 'DAY', count: 7 },
  RECENT_14_DAYS: { interval: 'DAY', count: 14 },
  RECENT_30_DAYS: { interval: 'DAY', count: 30 },
  RECENT_90_DAYS: { interval: 'DAY', count: 90 },
  RECENT_180_DAYS: { interval: 'DAY', count: 180 },
  RECENT_365_DAYS: { interval: 'DAY', count: 365 },
  RECENT_1825_DAYS: { interval: 'DAY', count: 1825 },
  RECENT_2_MONTHS: { interval: 'MONTH', count: 2 },
  RECENT_3_MONTHS: { interval: 'MONTH', count: 3 },
  RECENT_6_MONTHS: { interval: 'MONTH', count: 6 },
  RECENT_12_MONTHS: { interval: 'MONTH', count: 12 },
  RECENT_5_YEARS: { interval: 'YEAR', count: 5 },
} satisfies Record<CanonicalRecentRange, RecentIntervalCount>;

const isValidIntervalCount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 1;

const resolveRecentIntervalCount = (
  primaryDateRange: GeneratedReportRanges | undefined,
  primaryInterval: DynamicRangeIntervalTimestep | undefined,
  primaryCount: number | undefined,
): RecentIntervalCount | undefined => {
  const preset = primaryDateRange ? RECENT_PRESETS[primaryDateRange] : undefined;
  if (preset) return preset;

  return primaryDateRange === 'CUSTOM_RECENT' && primaryInterval && isValidIntervalCount(primaryCount)
    ? { interval: primaryInterval, count: primaryCount }
    : undefined;
};

const toPredefinedRange = (dateRange: GeneratedReportRanges): PREDEFINED_RANGE_VALUE | undefined => {
  switch (dateRange) {
    case 'THIS_MONTH':
    case 'THIS_QUARTER':
    case 'THIS_YEAR':
    case 'LAST_MONTH':
    case 'LAST_QUARTER':
    case 'LAST_YEAR':
    case 'RECENT_5_YEARS':
    case 'MONTH_TO_DATE':
    case 'QUARTER_TO_DATE':
    case 'YEAR_TO_DATE':
    case 'RECENT_2_MONTHS':
    case 'RECENT_3_MONTHS':
    case 'RECENT_6_MONTHS':
    case 'LAST_7_MONTHS':
    case 'RECENT_12_MONTHS':
    case 'LAST_24_MONTHS':
    case 'SAME_DAY_LAST_MONTH':
    case 'SAME_DAY_LAST_3_MONTHS':
    case 'SAME_DAY_LAST_6_MONTHS':
    case 'SAME_DAY_LAST_YEAR':
    case 'SAME_DAY_LAST_5_YEARS':
      return dateRange;
    default:
      return undefined;
  }
};

/** Resolve saved LAST_* aliases and current RECENT_* presets to the same canonical dates and metadata. */
export const resolveComparisonPrimaryRangeDates = (
  reportConfig: ReportConfigurationInputProps | undefined,
  currentDate: DateTime,
): ResolvedPrimaryRangeDates | undefined => {
  if (!reportConfig || !currentDate.isValid) return undefined;

  const { dateRangeInterval, dateRangeIntervalCount } = reportConfig;
  const dateRange = normalizeGeneratedReportRange(reportConfig.dateRange);
  if (!dateRange) return undefined;
  if (dateRange === 'CUSTOM') {
    return reportConfig.startOn && reportConfig.endOn
      ? { startOn: reportConfig.startOn, endOn: reportConfig.endOn, dateRange }
      : undefined;
  }

  if (dateRange === 'ALL_DATES') {
    // QCS does not resolve ALL_DATES to a concrete primary window. The general client date helper
    // aliases it to RECENT_5_YEARS, but using that here would show a comparison QCS will not produce.
    return undefined;
  }

  let interval;
  const recentPreset = RECENT_PRESETS[dateRange];
  const dayCount = recentPreset?.interval === 'DAY' ? recentPreset.count : undefined;
  if (dayCount !== undefined) {
    interval = {
      start: currentDate.startOf('day').minus({ days: dayCount - 1 }),
      end: currentDate.endOf('day'),
    };
  } else if (isGeneratedReportRangeTypeDynamic(dateRange)) {
    if (dateRange === 'CUSTOM_SINCE') {
      const startDate = DateTime.fromISO(reportConfig.startOn ?? '');
      if (!startDate.isValid) return undefined;
      interval = constructDynamicInterval({ type: dateRange, startDate }, currentDate);
    } else {
      if (!dateRangeInterval || !isValidIntervalCount(dateRangeIntervalCount)) return undefined;
      interval = constructDynamicInterval(
        { type: dateRange, interval: dateRangeInterval, count: dateRangeIntervalCount },
        currentDate,
      );
    }
  } else {
    const predefinedRange = toPredefinedRange(dateRange);
    if (!predefinedRange) return undefined;
    interval = constructPredefinedInterval({ value: predefinedRange }, currentDate);
  }

  const startOn = interval.start?.toISODate();
  const endOn = interval.end?.toISODate();
  return startOn && endOn ? { startOn, endOn, dateRange, dateRangeInterval, dateRangeIntervalCount } : undefined;
};

const resolvedDates = (start: DateTime, end: DateTime): ResolvedRangeDates | undefined => {
  const startOn = start.toISODate();
  const endOn = end.toISODate();
  return startOn && endOn ? { startOn, endOn } : undefined;
};

const advanceByPeriods = (start: DateTime, interval: DynamicRangeIntervalTimestep, count: number): DateTime => {
  switch (interval) {
    case 'DAY':
      return start.plus({ days: count - 1 });
    case 'WEEK':
      return start.plus({ days: 7 * count - 1 });
    case 'MONTH':
      return start.plus({ months: count - 1 }).endOf('month');
    case 'QUARTER':
      return start.plus({ months: 3 * count - 1 }).endOf('month');
    case 'YEAR':
      return start.plus({ years: count - 1 }).endOf('year');
  }
};

const retreatByPeriods = (start: DateTime, interval: DynamicRangeIntervalTimestep, count: number): DateTime => {
  switch (interval) {
    case 'DAY':
      return start.minus({ days: count });
    case 'WEEK':
      return start.minus({ weeks: count });
    case 'MONTH':
      return start.minus({ months: count });
    case 'QUARTER':
      return start.minus({ months: 3 * count });
    case 'YEAR':
      return start.minus({ years: count });
  }
};

/**
 * The concrete dates a comparison range covers, for display next to the primary range.
 *
 * Only the two relative modes are derivable from the primary range; `CUSTOM` carries its own dates
 * and is passed straight through. Every other mode returns `undefined` — a rolling range resolves
 * against the current date rather than the primary range, and guessing it here would show the user
 * a span the server will not agree with.
 */
export const resolveComparisonRangeDates = (
  primary: ResolvedPrimaryRangeDates | undefined,
  range: ComparisonRange | undefined,
): ResolvedRangeDates | undefined => {
  if (!range) return undefined;

  if (range.dateRange === 'CUSTOM') {
    return range.startOn && range.endOn ? { startOn: range.startOn, endOn: range.endOn } : undefined;
  }

  if (!isRelativeMode(range.dateRange) || !primary) return undefined;

  const start = DateTime.fromISO(primary.startOn);
  const end = DateTime.fromISO(primary.endOn);
  if (!start.isValid || !end.isValid || end < start) return undefined;
  const span = end.diff(start, 'days').days + 1;
  const primaryDateRange = primary.dateRange;

  if (range.dateRange === 'PRIOR_YEAR_PERIOD') {
    const recentIntervalCount = resolveRecentIntervalCount(
      primaryDateRange,
      primary.dateRangeInterval,
      primary.dateRangeIntervalCount,
    );
    if (recentIntervalCount) {
      const comparisonStart = start.minus({ years: 1 });
      if (recentIntervalCount.interval === 'DAY' || recentIntervalCount.interval === 'WEEK') {
        return resolvedDates(comparisonStart, end.minus({ years: 1 }));
      }

      // Month, quarter, and year ranges retain the exact primary start one year earlier, then
      // extend through the full configured period boundary.
      return resolvedDates(
        comparisonStart,
        advanceByPeriods(comparisonStart, recentIntervalCount.interval, recentIntervalCount.count),
      );
    }

    if (
      primaryDateRange === 'CUSTOM_LAST' &&
      primary.dateRangeInterval &&
      isValidIntervalCount(primary.dateRangeIntervalCount)
    ) {
      const comparisonStart = start.minus({ years: 1 });
      return resolvedDates(
        comparisonStart,
        advanceByPeriods(comparisonStart, primary.dateRangeInterval, primary.dateRangeIntervalCount),
      );
    }

    // CUSTOM, CUSTOM_SINCE, and every other non-special-cased preset shift each endpoint
    // independently. A leap-day-spanning CUSTOM range may therefore shrink by one day.
    return resolvedDates(start.minus({ years: 1 }), end.minus({ years: 1 }));
  }

  if (primaryDateRange === 'THIS_YEAR' || primaryDateRange === 'LAST_YEAR' || primaryDateRange === 'YEAR_TO_DATE') {
    return resolvedDates(start.minus({ years: 1 }), end.minus({ years: 1 }));
  }

  if (primaryDateRange === 'THIS_QUARTER' || primaryDateRange === 'LAST_QUARTER') {
    const comparisonStart = start.minus({ months: 3 });
    return resolvedDates(comparisonStart, comparisonStart.plus({ months: 2 }).endOf('month'));
  }

  if (primaryDateRange === 'THIS_MONTH' || primaryDateRange === 'LAST_MONTH') {
    const comparisonStart = start.minus({ months: 1 }).startOf('month');
    return resolvedDates(comparisonStart, comparisonStart.endOf('month'));
  }

  if (primaryDateRange === 'MONTH_TO_DATE') {
    const comparisonStart = start.minus({ months: 1 });
    const comparisonEnd = comparisonStart.set({
      day: Math.min(end.day, comparisonStart.daysInMonth ?? end.day),
    });
    return resolvedDates(comparisonStart, comparisonEnd);
  }

  if (primaryDateRange === 'QUARTER_TO_DATE') {
    return resolvedDates(start.minus({ months: 3 }), end.minus({ months: 3 }));
  }

  if (
    primaryDateRange === 'CUSTOM_LAST' &&
    primary.dateRangeInterval &&
    isValidIntervalCount(primary.dateRangeIntervalCount) &&
    ['MONTH', 'QUARTER', 'YEAR'].includes(primary.dateRangeInterval)
  ) {
    const comparisonStart = retreatByPeriods(start, primary.dateRangeInterval, primary.dateRangeIntervalCount);
    return resolvedDates(comparisonStart, start.minus({ days: 1 }));
  }

  const recentIntervalCount = resolveRecentIntervalCount(
    primaryDateRange,
    primary.dateRangeInterval,
    primary.dateRangeIntervalCount,
  );
  if (recentIntervalCount) {
    // The primary includes a partial current period; the comparison is the immediately preceding
    // full interval/count window, ending at the boundary just before the primary starts.
    const comparisonStart = retreatByPeriods(start, recentIntervalCount.interval, recentIntervalCount.count);
    return resolvedDates(comparisonStart, start.minus({ days: 1 }));
  }

  // CUSTOM, CUSTOM_SINCE, frontend-only rolling ranges, and every other preset use the immediately
  // preceding window with the same inclusive duration.
  const comparisonEnd = start.minus({ days: 1 });
  return resolvedDates(comparisonEnd.minus({ days: span - 1 }), comparisonEnd);
};
